import React, { useState, useEffect } from 'react';
import { 
  HelpCircle,
  Map,
  Users,
  Clock,
  FileText,
  Share2,
  Printer,
  Send,
  ChevronRight,
  Bitcoin,
  TrendingUp,
  Home,
  Car,
  PawPrint,
  Globe,
  FolderOpen,
  Briefcase,
  Heart,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useUser } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.lifely.guide';

// Tab configuration
const TABS = [
  { id: 'getting-started', label: 'Getting Started', icon: HelpCircle },
  { id: 'maps', label: 'Maps', icon: Map },
  { id: 'crypto', label: 'Crypto Maps', icon: Bitcoin },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'continuity', label: 'Continuity', icon: Clock },
  { id: 'formal', label: 'Formal Arrangements', icon: FileText },
  { id: 'export', label: 'Export & Print', icon: Printer },
  { id: 'sharing', label: 'Sharing', icon: Share2 },
  { id: 'contact-us', label: 'Contact Us', icon: Send },
];

// Map types with icons - matches actual app maps
const MAP_TYPES = [
  { id: 'crypto', name: 'Cryptocurrency', icon: Bitcoin, color: 'text-orange-500' },
  { id: 'shares', name: 'Shares & Investments', icon: TrendingUp, color: 'text-green-500' },
  { id: 'property', name: 'Property', icon: Home, color: 'text-blue-500' },
  { id: 'vehicles', name: 'Vehicles', icon: Car, color: 'text-purple-500' },
  { id: 'pets', name: 'Pets', icon: PawPrint, color: 'text-pink-500' },
  { id: 'social', name: 'Social & Online Accounts', icon: Globe, color: 'text-cyan-500' },
  { id: 'art', name: 'Art & Collectables', icon: Briefcase, color: 'text-amber-500' },
  { id: 'physical', name: 'Physical Records & Docs', icon: FolderOpen, color: 'text-slate-500' },
  { id: 'insurance', name: 'Insurance Policies', icon: Shield, color: 'text-emerald-500' },
  { id: 'liabilities', name: 'Liabilities & Debts', icon: AlertTriangle, color: 'text-yellow-500' },
  { id: 'final-wishes', name: 'Final Wishes', icon: Heart, color: 'text-red-500' },
  { id: 'other-info', name: 'Other Information', icon: FolderOpen, color: 'text-indigo-500' },
];

const Help = () => {
  const { user: clerkUser } = useUser();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (clerkUser) {
      const fullName = clerkUser?.fullName || '';
      const email = clerkUser?.primaryEmailAddress?.emailAddress || '';
      setContactForm(prev => ({
        ...prev,
        name: fullName || prev.name,
        email: email || prev.email,
      }));
    }
  }, [clerkUser]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Message sent! We\'ll get back to you soon.');
        // Only reset subject and message, keep name and email from Clerk
        setContactForm(prev => ({ ...prev, subject: '', message: '' }));
      } else {
        toast.error(data.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'getting-started':
        return <GettingStartedTab />;
      case 'maps':
        return <MapsTab />;
      case 'crypto':
        return <CryptoTab />;
      case 'contacts':
        return <ContactsTab />;
      case 'continuity':
        return <ContinuityTab />;
      case 'formal':
        return <FormalArrangementsTab />;
      case 'export':
        return <ExportTab />;
      case 'sharing':
        return <SharingTab />;
      case 'contact-us':
        return (
          <ContactUsTab 
            form={contactForm} 
            setForm={setContactForm} 
            onSubmit={handleContactSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return <GettingStartedTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif text-foreground mb-2">
            Help & Support
          </h1>
          <p className="text-muted-foreground">
            Learn how to use Lifely and get the most out of your account
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <nav className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      isActive 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{tab.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Card className="border border-border">
              <CardContent className="p-6 md:p-8">
                {renderTabContent()}
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </div>
  );
};

// Tab Components
const GettingStartedTab = () => (
  <div className="space-y-6" data-testid="getting-started-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Welcome to Lifely</h2>
      <p className="text-muted-foreground leading-relaxed">
        Lifely is a calm, privacy-first platform for mapping your assets, responsibilities, and intentions. 
        It's designed to help you document what matters without storing sensitive information like passwords or private keys.
      </p>
    </div>

    {/* Dashboard screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/1_-_dashboard.png" 
        alt="Lifely Dashboard" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">Your Lifely dashboard</p>
    </div>

    <div className="bg-primary/5 rounded-lg p-6 border border-primary/10">
      <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
        <Shield className="w-5 h-5 text-primary" />
        Privacy First
      </h3>
      <p className="text-sm text-muted-foreground">
        Lifely never stores passwords, seed phrases, private keys, or sensitive credentials. 
        You note <em>where</em> things are managed, not the secrets themselves.
      </p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4">Quick Start Guide</h3>
      <div className="space-y-4">
        <QuickStartStep 
          number={1} 
          title="Explore Your Dashboard"
          description="Your dashboard shows an overview of all your maps, contacts, and continuity status. The progress stepper shows what's still to set up."
        />
        <QuickStartStep 
          number={2} 
          title="Add Trusted Contacts"
          description="Add people you trust who might need to know about your assets, family, your lawyer, executor, or close friends."
        />
        <QuickStartStep 
          number={3} 
          title="Fill In Your Maps"
          description="Go to Maps and choose categories relevant to you, crypto, property, vehicles, insurance, liabilities, and more. Document what you have without storing secrets."
        />
        <QuickStartStep 
          number={4} 
          title="Set Up Continuity Heartbeat"
          description="Configure check-in reminders so Lifely can notify your nominated contacts if you stop responding, a key feature for peace of mind."
        />
        <QuickStartStep 
          number={5} 
          title="Complete Your Formal Arrangements"
          description="Record your will, Power of Attorney, and key advisors so the right people know where to look."
        />
        <QuickStartStep 
          number={6} 
          title="Export Your Records"
          description="Create a lawyer-friendly PDF or CSV export of everything you've documented. Keep a copy with your will."
        />
      </div>
    </div>

    <div className="border-t border-border pt-6">
      <h3 className="text-lg font-semibold text-foreground mb-3">Understanding the Interface</h3>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Dashboard</strong> - Overview of your maps, contacts, and status</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Maps</strong> - 12 categories for organizing your assets and accounts</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Contacts</strong> - Trusted people who can be given access to your information</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Continuity Heartbeat</strong> - Check-in system that notifies nominees if you stop responding</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Formal Arrangements</strong> - Will, POA, advisors, and legal document tracking</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Export</strong> - Generate lawyer-friendly PDFs and CSV files of all your records</span>
        </li>
      </ul>
    </div>
  </div>
);

const MapsTab = () => (
  <div className="space-y-6" data-testid="maps-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Creating & Managing Maps</h2>
      <p className="text-muted-foreground leading-relaxed">
        Maps are the core of Lifely. Each map category helps you organize different types of assets, 
        accounts, and responsibilities. You can add entries, mark priorities, and assign contacts to each.
      </p>
    </div>

    {/* Maps screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/4_-_maps_all.png" 
        alt="Lifely Maps Overview" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">All 12 maps, including the Formal Arrangements tile</p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4">All 12 Map Types</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {MAP_TYPES.map((type) => {
          const Icon = type.icon;
          return (
            <div 
              key={type.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border"
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${type.color}`} />
              <span className="text-sm text-foreground">{type.name}</span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        The <strong>Formal Arrangements</strong> tile links to your will, POA, and legal documents and is shown separately at the bottom of the maps page.
      </p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">How to Create a Map Entry</h3>
      <ol className="space-y-3 text-muted-foreground">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">1</span>
          <span>Navigate to <strong>Maps</strong> from the main menu</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">2</span>
          <span>Select a map category (e.g., Crypto, Property, Vehicles)</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">3</span>
          <span>Click <strong>Add Entry</strong> to create a new item</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">4</span>
          <span>Fill in the details, name, type, location, and any notes</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">5</span>
          <span>Set the priority (Normal, High, or Critical) to help contacts know what matters most</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">6</span>
          <span>Optionally add a <strong>First Action</strong> - instructions for whoever finds this entry</span>
        </li>
      </ol>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="p-4 rounded-lg bg-muted/30 border border-border">
        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          Liabilities & Debts
        </h4>
        <p className="text-sm text-muted-foreground">
          Track mortgages, car finance, personal loans, and informal cash loans. Fields include lender name, 
          outstanding balance, interest rate, and monthly payment. If you mark a property as "Mortgaged" or a 
          vehicle as "Financed", Lifely will prompt you to add the details here.
        </p>
      </div>
      <div className="p-4 rounded-lg bg-muted/30 border border-border">
        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <Heart className="w-4 h-4 text-red-500" />
          Final Wishes
        </h4>
        <p className="text-sm text-muted-foreground">
          Document your personal wishes including bequests and donations to specific people or organisations, 
          funeral preferences, sentimental items, and any other final wishes.
        </p>
      </div>
      <div className="p-4 rounded-lg bg-muted/30 border border-border">
        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-indigo-500" />
          Other Information
        </h4>
        <p className="text-sm text-muted-foreground">
          A flexible catch-all for anything that doesn't fit elsewhere, loans you've made to others (receivables), 
          risks and concerns (people or situations to be aware of), subscriptions, memberships, and general notes.
        </p>
      </div>
    </div>

    <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
      <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        Important Note
      </h4>
      <p className="text-sm text-amber-700 dark:text-amber-300">
        Never store actual passwords, seed phrases, or private keys in Lifely. Instead, note where these are stored 
        (e.g., "1Password vault", "hardware wallet in safe deposit box").
      </p>
    </div>
  </div>
);

const CryptoTab = () => (
  <div className="space-y-6" data-testid="crypto-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
        <Bitcoin className="w-8 h-8 text-orange-500" />
        Cryptocurrency Maps
      </h2>
      <p className="text-muted-foreground leading-relaxed">
        Cryptocurrency is uniquely challenging for estate planning and continuity. Unlike traditional assets, 
        crypto can be permanently lost if access information isn't properly documented and passed on. 
        Lifely's Crypto Maps are designed specifically for this challenge.
      </p>
    </div>

    <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 rounded-lg p-6 border border-orange-200 dark:border-orange-800">
      <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
        <Shield className="w-5 h-5 text-orange-500" />
        Why Crypto Mapping Matters
      </h3>
      <ul className="space-y-2 text-sm text-muted-foreground">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <span><strong>Self-custody means self-responsibility</strong> - No bank or institution can recover your assets</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <span><strong>Seed phrases are single points of failure</strong> - If lost, your crypto is gone forever</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <span><strong>Heirs may not know crypto exists</strong> - Without documentation, assets can be overlooked</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <span><strong>Technical complexity creates barriers</strong> - Clear instructions are essential for non-technical contacts</span>
        </li>
      </ul>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-4">What to Document in Your Crypto Map</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Exchange Accounts</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Exchange name (Coinbase, Kraken, etc.)</li>
            <li>• Approximate holdings</li>
            <li>• Email used for account</li>
            <li>• Where 2FA recovery is stored</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Hardware Wallets</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Device type (Ledger, Trezor, etc.)</li>
            <li>• Physical location of device</li>
            <li>• Where seed phrase backup is stored</li>
            <li>• PIN location (if separate)</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Software Wallets</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Wallet name (MetaMask, Trust, etc.)</li>
            <li>• Which networks/chains</li>
            <li>• Where seed phrase is stored</li>
            <li>• Approximate value or purpose</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">DeFi & Staking</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Protocols used</li>
            <li>• Which wallet connects to them</li>
            <li>• Lock-up periods if any</li>
            <li>• Instructions to withdraw</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        Critical: What NOT to Store in Lifely
      </h4>
      <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
        <li>❌ Seed phrases / recovery phrases</li>
        <li>❌ Private keys</li>
        <li>❌ Exchange passwords</li>
        <li>❌ 2FA backup codes</li>
      </ul>
      <p className="text-sm text-red-700 dark:text-red-300 mt-3">
        Instead, note <em>where</em> these are securely stored (e.g., "Seed phrase in fireproof safe, master bedroom closet").
      </p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Best Practices for Crypto Continuity</h3>
      <ol className="space-y-3 text-muted-foreground">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm flex items-center justify-center font-medium">1</span>
          <span><strong>Create detailed entries</strong> - Be specific about what exists and where access is stored</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm flex items-center justify-center font-medium">2</span>
          <span><strong>Mark high-value items as Critical</strong> - Helps contacts prioritize what to secure first</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm flex items-center justify-center font-medium">3</span>
          <span><strong>Write for non-technical readers</strong> - Your contact may not understand crypto terminology</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm flex items-center justify-center font-medium">4</span>
          <span><strong>Update regularly</strong> - Crypto portfolios change; keep your map current</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm flex items-center justify-center font-medium">5</span>
          <span><strong>Enable continuity check-ins</strong> - Crypto waits for no one; automated alerts protect your assets</span>
        </li>
      </ol>
    </div>

    <div className="border-t border-border pt-6">
      <h3 className="text-lg font-semibold text-foreground mb-3">Example Crypto Entry</h3>
      <div className="bg-muted/30 rounded-lg p-4 border border-border font-mono text-sm">
        <p className="text-foreground font-semibold">Ledger Nano X - Primary Holdings</p>
        <p className="text-muted-foreground mt-2">
          <strong>Priority:</strong> Critical<br/>
          <strong>Location:</strong> Home office desk drawer<br/>
          <strong>Contents:</strong> BTC, ETH, and various altcoins (~$XX,XXX)<br/>
          <strong>Seed Phrase:</strong> Metal backup in safe deposit box at First National Bank (Box #1234)<br/>
          <strong>PIN:</strong> Written on paper in fireproof safe, master bedroom<br/>
          <strong>Instructions:</strong> Connect via USB to computer, download Ledger Live from ledger.com, enter PIN to access. 
          If device lost, use seed phrase to recover on new device.
        </p>
      </div>
    </div>
  </div>
);

const ContactsTab = () => (
  <div className="space-y-6" data-testid="contacts-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Managing Contacts</h2>
      <p className="text-muted-foreground leading-relaxed">
        Contacts are the trusted people in your life who may need access to your information. 
        You control exactly what each contact can see and when they're notified.
      </p>
    </div>

    {/* Contacts screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/2_-_contacts.png" 
        alt="Lifely Contacts" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">The Contacts page</p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Adding a Contact</h3>
      <ol className="space-y-3 text-muted-foreground">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">1</span>
          <span>Go to <strong>Contacts</strong> in the main menu</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">2</span>
          <span>Click <strong>Add Contact</strong></span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">3</span>
          <span>Enter their name, email, phone (optional), and relationship</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">4</span>
          <span>Add any notes about this person's role</span>
        </li>
      </ol>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Contact Roles</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Consider adding contacts for different purposes:
      </p>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start gap-2">
          <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Primary contact</strong> - First person to be notified if you don't check in</span>
        </li>
        <li className="flex items-start gap-2">
          <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Secondary contact</strong> - Backup if primary doesn't respond</span>
        </li>
        <li className="flex items-start gap-2">
          <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Professional advisors</strong> - Lawyer, accountant, financial advisor</span>
        </li>
        <li className="flex items-start gap-2">
          <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
          <span><strong>Family members</strong> - Spouse, children, siblings, parents</span>
        </li>
      </ul>
    </div>

    <div className="bg-muted/30 rounded-lg p-4 border border-border">
      <h4 className="font-medium text-foreground mb-2">Assigning Contacts to Maps</h4>
      <p className="text-sm text-muted-foreground">
        When creating or editing a map entry, you can assign specific contacts. 
        This determines who can see that entry when sharing is enabled or continuity is triggered.
      </p>
    </div>
  </div>
);

const ContinuityTab = () => (
  <div className="space-y-6" data-testid="continuity-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Continuity Heartbeat</h2>
      <p className="text-muted-foreground leading-relaxed">
        The Continuity Heartbeat is Lifely's automated check-in system. It periodically sends you a reminder 
        to confirm you're still active. If you don't respond after a set number of attempts, your nominated 
        contacts are notified automatically.
      </p>
    </div>

    {/* Continuity screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/5_-_continuity.png" 
        alt="Lifely Continuity Heartbeat" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">The Continuity Heartbeat setup page</p>
    </div>

    <div className="bg-primary/5 rounded-lg p-6 border border-primary/10">
      <h3 className="font-semibold text-foreground mb-3">How It Works</h3>
      <ol className="space-y-3 text-sm text-muted-foreground">
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">1</span>
          <span>You set a check-in interval (e.g. every 1 month, 3 months, 6 months). <em>Monthly is recommended for most people.</em></span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">2</span>
          <span>Lifely automatically detects your timezone and lets you choose a preferred notification time</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">3</span>
          <span>When it's time to check in, Lifely sends you an email with a one-click confirmation link</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">4</span>
          <span>If you don't respond, <strong>follow-up reminders are sent every 24 hours</strong> (3 by default, customisable from 1-5). Responding to any reminder counts as a check-in and resets the timer.</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">5</span>
          <span>If all reminders go unanswered, your nominated contacts are notified, but <strong>no data is shared yet</strong>. You receive a "last chance" email with a link to cancel.</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">6</span>
          <span>After a <strong>48-hour grace period</strong>, if you haven't cancelled, each nominee receives a secure, one-time link to view only the data you've configured in your Sharing Settings. The link expires after 7 days.</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">7</span>
          <span>After escalation completes, your continuity cycle automatically resets to your configured frequency.</span>
        </li>
      </ol>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Setting Up Continuity Heartbeat</h3>
      <p className="text-sm text-muted-foreground mb-4">
        The setup wizard guides you through three simple steps:
      </p>
      <ol className="space-y-3 text-muted-foreground">
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">1</span>
          <span><strong>Frequency</strong> - Choose how often to check in and your timezone (auto-detected)</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">2</span>
          <span><strong>Contact Method</strong> - Confirm your email address for receiving check-in reminders</span>
        </li>
        <li className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">3</span>
          <span><strong>Nominees</strong> - Select which contacts should be notified if you don't respond. The default is 3 check-in attempts before escalation - you can change this (1-5) via <strong>Continuity Settings</strong>.</span>
        </li>
      </ol>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Emergency Access</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Lifely includes an Emergency Access feature for situations where a trusted person needs expedited access, for example, if you're incapacitated or have passed away.
      </p>
      <ul className="space-y-2 text-sm text-muted-foreground">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span>Generate a unique emergency access code and QR from your Continuity page</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span>Use <strong>Print PDF</strong> to generate a printable Emergency Access Card - store it with your will or in a sealed envelope for a trusted person</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span>Only share this card with someone you deeply trust, a family member, close friend, or your executor</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span>If someone uses the code, you receive an email notification with 48 hours to deny the request</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span>You can also deny pending requests directly from the app</span>
        </li>
      </ul>
    </div>

    <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
      <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Recommended Settings</h4>
      <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
        <li>• <strong>Check-in interval:</strong> Monthly is a good balance for most users</li>
        <li>• <strong>Rechecks:</strong> 3 is the default - follow-ups are sent every 24 hours after a missed check-in</li>
        <li>• <strong>Multiple nominees:</strong> Add at least 2 contacts for redundancy</li>
        <li>• <strong>Sharing settings:</strong> Configure exactly which maps each nominee can see - this controls what data is shared during escalation</li>
        <li>• <strong>Test your email:</strong> Use the verification feature to confirm emails are delivered</li>
        <li>• <strong>Emergency card:</strong> Print the PDF and store it with your will or important documents</li>
      </ul>
    </div>
  </div>
);

const FormalArrangementsTab = () => (
  <div className="space-y-6" data-testid="formal-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Formal Arrangements</h2>
      <p className="text-muted-foreground leading-relaxed">
        Track your legal documents and formal arrangements in one place. Lifely doesn't replace legal documents - it helps you record what exists and where to find it.
      </p>
    </div>

    {/* Formal arrangements screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/3_-_formal_arrangements.png" 
        alt="Formal Arrangements" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">The Formal Arrangements page</p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Available Tabs</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Will</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Whether you have a will</li>
            <li>• Where the original is stored</li>
            <li>• Date created and last updated</li>
            <li>• Who holds the will</li>
            <li>• Will scope (assets covered)</li>
            <li>• Primary and secondary executors</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Power of Attorney</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Type (financial, medical, enduring)</li>
            <li>• When it takes effect</li>
            <li>• Appointed attorney</li>
            <li>• Successor attorney</li>
            <li>• Law firm details</li>
            <li>• Additional POA holders</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Family</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Children and dependents</li>
            <li>• Date of birth for each</li>
            <li>• Intended guardians</li>
            <li>• Godparents</li>
            <li>• Special needs or arrangements</li>
            <li>• Guardianship status</li>
          </ul>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2">Advisors</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Legal advisor / Solicitor</li>
            <li>• Accountant</li>
            <li>• Financial advisor</li>
            <li>• Tax advisor</li>
            <li>• Insurance advisor</li>
            <li>• Other professionals</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="p-4 rounded-lg bg-muted/30 border border-border">
      <h4 className="font-medium text-foreground mb-2">Other Documents</h4>
      <p className="text-sm text-muted-foreground mb-2">
        Track additional legal documents and arrangements:
      </p>
      <ul className="text-sm text-muted-foreground space-y-1">
        <li>• Trusts and trust documents</li>
        <li>• Prenuptial agreements</li>
        <li>• Business succession plans</li>
        <li>• Whether Lifely is referenced in your will</li>
        <li>• Any additional notes</li>
      </ul>
    </div>

    <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
      <h4 className="font-medium text-foreground mb-2">Will Review Reminders</h4>
      <p className="text-sm text-muted-foreground mb-2">
        You can set a reminder date on the Will tab to prompt you to review your Will periodically. 
        When the date arrives, Lifely sends you an email reminder with a link back to your Formal Arrangements page.
      </p>
      <ul className="text-sm text-muted-foreground space-y-1">
        <li>• The reminder is sent to your primary email, or an alternative email if configured</li>
        <li>• After sending, the reminder is automatically cleared - set a new date for your next review</li>
        <li>• We recommend reviewing your Will at least once a year, or after any major life event</li>
      </ul>
    </div>

    <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
      <h4 className="font-medium text-foreground mb-2">Exporting Formal Arrangements</h4>
      <p className="text-sm text-muted-foreground">
        All information from Formal Arrangements is included in the "Export for Lawyer" feature. 
        The PDF export includes detailed tables for POA, children/dependents, advisors, and other documents.
      </p>
    </div>

    <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
      <p className="text-sm text-amber-700 dark:text-amber-300">
        <strong>Note:</strong> Lifely is not a legal service and doesn't provide legal advice. 
        The Formal Arrangements section is for record-keeping and organization only. 
        Always consult with qualified legal professionals for estate planning.
      </p>
    </div>
  </div>
);

const ExportTab = () => (
  <div className="space-y-6" data-testid="export-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Export & Print</h2>
      <p className="text-muted-foreground leading-relaxed">
        Create comprehensive exports of your maps and information. Perfect for sharing with lawyers, 
        keeping physical backups, or providing to specific contacts.
      </p>
    </div>

    {/* Export screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/6_-_export.png" 
        alt="Lifely Export Page" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">The Export & Print page</p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Export Options</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Export for Lawyer
          </h4>
          <p className="text-sm text-muted-foreground">
            One-click comprehensive PDF with all your formal arrangements, contacts, and asset maps. 
            Includes POA information, children & guardians, all advisors, and a legal disclaimer page.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Export for Contact
          </h4>
          <p className="text-sm text-muted-foreground">
            Generate a PDF for a specific contact showing only assets linked to them via wishes or notes. 
            Great for sharing relevant information with individual beneficiaries.
          </p>
        </div>
      </div>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Custom Export</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Choose exactly what to include in your export:
      </p>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span><strong>Will & Legal Status</strong> - Will details, POA, executors, children, guardians, trusts</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span><strong>Contacts</strong> - All your trusted contacts with their details</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <span><strong>Asset Maps</strong> - Select individual maps to include, including Liabilities & Debts</span>
        </li>
      </ul>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Export Formats</h3>
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border">
          <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-foreground">PDF</strong>
            <p className="text-sm text-muted-foreground">
              Professional format with proper text wrapping, page breaks, and detailed tables. 
              Every PDF includes a <strong>Legal Disclaimer & Handling Notice</strong> as the final page.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border">
          <Printer className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-foreground">CSV</strong>
            <p className="text-sm text-muted-foreground">
              Spreadsheet format with all fields. Great for data analysis or importing into other systems.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        Security & Liability Notice
      </h4>
      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
        Before every export, Lifely shows a <strong>security reminder</strong> about handling sensitive data. 
        Every PDF also includes a full legal disclaimer page. Please read and follow the precautions:
      </p>
      <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
        <li>• Only print in a private, secure environment</li>
        <li>• Store digital copies in encrypted or password-protected locations</li>
        <li>• Only share with authorised individuals (lawyer, executor, immediate family)</li>
        <li>• Shred printed copies when no longer needed</li>
      </ul>
      <p className="text-sm text-red-700 dark:text-red-300 mt-2">
        Lifely cannot be held responsible for how exported data is handled after download.
      </p>
    </div>
  </div>
);

const SharingTab = () => (
  <div className="space-y-6" data-testid="sharing-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Sharing Settings</h2>
      <p className="text-muted-foreground leading-relaxed">
        Control what your contacts can see and when. Sharing settings give you fine-grained 
        control over your information.
      </p>
    </div>

    {/* Sharing screenshot */}
    <div className="rounded-lg overflow-hidden border border-border shadow-sm">
      <img 
        src="/help/7_-_sharing.png" 
        alt="Lifely Sharing Settings" 
        className="w-full object-cover"
        loading="lazy"
      />
      <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">The Sharing settings page</p>
    </div>

    <div>
      <h3 className="text-lg font-semibold text-foreground mb-3">Sharing Options</h3>
      <ul className="space-y-3 text-muted-foreground">
        <li className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Share2 className="w-3 h-3 text-primary" />
          </div>
          <div>
            <strong className="text-foreground">Per-contact sharing</strong>
            <p className="text-sm">Assign specific maps and entries to individual contacts</p>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Clock className="w-3 h-3 text-primary" />
          </div>
          <div>
            <strong className="text-foreground">Continuity-triggered sharing</strong>
            <p className="text-sm">When continuity check-ins are missed and the 48-hour grace period passes, each nominee receives a secure link to view <strong>only the maps and data you've selected here</strong>, nothing more. This is how you control exactly what each person sees.</p>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Shield className="w-3 h-3 text-primary" />
          </div>
          <div>
            <strong className="text-foreground">Read-only access</strong>
            <p className="text-sm">Contacts can view but never edit your information</p>
          </div>
        </li>
      </ul>
    </div>

    <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
      <h4 className="font-medium text-foreground mb-2">How to Configure Sharing</h4>
      <ol className="text-sm text-muted-foreground space-y-2">
        <li>1. Go to <strong>Sharing</strong> in the main menu</li>
        <li>2. Review the default sharing settings</li>
        <li>3. Customize which contacts can see which maps</li>
        <li>4. Set conditions for when sharing is activated</li>
      </ol>
    </div>
  </div>
);

const ContactUsTab = ({ form, setForm, onSubmit, isSubmitting }) => (
  <div className="space-y-6" data-testid="contact-us-content">
    <div>
      <h2 className="text-2xl font-serif text-foreground mb-4">Contact Us</h2>
      <p className="text-muted-foreground leading-relaxed">
        Have a question, suggestion, or need help? We're here for you. 
        Fill out the form below and we'll get back to you as soon as possible.
      </p>
    </div>

    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="contact-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            data-testid="contact-email-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        <Input
          id="subject"
          placeholder="What's this about?"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          required
          data-testid="contact-subject-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          placeholder="Tell us more..."
          rows={5}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          required
          data-testid="contact-message-input"
        />
      </div>

      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full md:w-auto"
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>

    <div className="border-t border-border pt-6">
      <h3 className="text-lg font-semibold text-foreground mb-3">Other Ways to Reach Us</h3>
      <div className="space-y-3 text-muted-foreground">
        <a 
          href="mailto:support@lifely.guide" 
          className="flex items-center gap-2 hover:text-primary transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          support@lifely.guide
        </a>
        <a 
          href="https://lifely.guide" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:text-primary transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          lifely.guide
        </a>
      </div>
    </div>
  </div>
);

// Helper Components
const QuickStartStep = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
      {number}
    </div>
    <div>
      <h4 className="font-medium text-foreground">{title}</h4>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
    </div>
  </div>
);

export default Help;
